import { Injectable } from "@angular/core";
import { JwtHelperService } from "@auth0/angular-jwt";
import { RouterDataResolved } from "@ngxs/router-plugin";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { EMPTY } from "rxjs";
import { tap } from "rxjs/operators";
import { IRefreshToken } from "src/app/model/IAuth";
import { IUser } from "../../model/IUser";
import { IAuthenticationResult } from "../../model/IUserLoginEvent";
import { AuthV1Service } from "../service/auth-v1.service";
import { AuthenticatedUserService } from "../service/authenticated-user.service";
import {
	StoredAuthRemoveAction,
	StoredAuthSetAction,
	StoredAuthUserRefreshTokenAction,
} from "./stored-auth.actions";

export class StoredAuthStateModel {
	public userToken: IAuthenticationResult;
	public user: IUser;
}

@State<StoredAuthStateModel>({
	name: "storedAuth",
	defaults: {
		userToken: null,
		user: null,
	},
})
@Injectable()
export class StoredAuthState {
	constructor(
		private authV1Service: AuthV1Service,
		private jwtHelperService: JwtHelperService,
		private authenticatedUserService: AuthenticatedUserService
	) {}
	@Selector()
	static authIdToken(state: StoredAuthStateModel): string {
		return state.userToken?.IdToken;
	}
	@Selector()
	static authRefreshToken(state: StoredAuthStateModel): string {
		return state.userToken?.RefreshToken;
	}

	@Selector()
	static isUserAuthenticated(state: StoredAuthStateModel): boolean {
		const jwtHelperService = new JwtHelperService();
		return (
			!!state.userToken &&
			!jwtHelperService.isTokenExpired(state.userToken?.IdToken)
		);
	}

	@Selector()
	static token(state: StoredAuthStateModel): IAuthenticationResult {
		return state.userToken;
	}
	@Selector()
	static user(state: StoredAuthStateModel): IUser {
		return state.user;
	}
	@Selector()
	static groups(state: StoredAuthStateModel): string[] {
		if (!state.user) {
			return [];
		}
		return state.user["cognito:groups"];
	}
	@Selector()
	static userLoginDetails(state: StoredAuthStateModel): string {
		return state.user.email || state.user.phone_number;
	}

	@Selector()
	static state(state: StoredAuthStateModel): StoredAuthStateModel {
		return state;
	}

	@Action(RouterDataResolved)
	RouterDataResolved(
		ctx: StateContext<StoredAuthStateModel>,
		action: RouterDataResolved
	) {
		const { userToken } = ctx.getState();
		if (!userToken) {
			return EMPTY;
		}
		if (this.jwtHelperService.isTokenExpired()) {
			return ctx.dispatch([new StoredAuthUserRefreshTokenAction()]);
		}
	}

	@Action(StoredAuthUserRefreshTokenAction)
	StoredAuthUserRefreshTokenAction(
		ctx: StateContext<StoredAuthStateModel>,
		action: StoredAuthUserRefreshTokenAction
	) {
		const { userToken, user } = ctx.getState();
		if (!userToken || !user) {
			return EMPTY;
		}

		const { RefreshToken } = userToken;

		const data: IRefreshToken = {
			refreshToken: RefreshToken,
			userid: user["cognito:username"],
		};
		return this.authenticatedUserService.RefreshToken(data).pipe(
			tap((result: IAuthenticationResult) => {
				if (result) {
					this.authV1Service.setAuthData(result);
					return ctx.dispatch(new StoredAuthSetAction(result));
				} else {
					return EMPTY;
				}
			})
		);
	}

	@Action(StoredAuthSetAction)
	StoredAuthSetAction(
		ctx: StateContext<StoredAuthStateModel>,
		action: StoredAuthSetAction
	) {
		if (!action.payload.IdToken || action.payload.IdToken === "undefined") {
			ctx.patchState({
				user: null,
				userToken: null,
			});
			return;
		}
		const decodedToken = this.jwtHelperService.decodeToken(
			action.payload.IdToken
		) as IUser;

		// this.authV1Service.setAuthData(action.payload);

		ctx.patchState({
			user: decodedToken,
			userToken: action.payload,
		});
	}

	@Action(StoredAuthRemoveAction)
	StoredAuthRemoveAction(
		ctx: StateContext<StoredAuthStateModel>,
		action: StoredAuthRemoveAction
	) {
		this.authV1Service.clearAuthData();
		ctx.patchState({
			user: null,
			userToken: null,
		});
	}
}
